import { formatDateOnlyForPicker, normalizeDateOnly } from './dateOnly';

export const POLICY_CANCEL_LOG_DATE_RANGE_PRESETS = [
  { text: 'Today', value: 'today' },
  { text: 'Last 7 Days', value: 'last_7_days' },
  { text: 'Last 30 Days', value: 'last_30_days' },
  { text: 'This Month', value: 'this_month' },
  { text: 'Last Month', value: 'last_month' },
  { text: 'Custom Range', value: 'custom' },
];

export const POLICY_CANCEL_LOG_DATE_RANGE_PRESET_LABELS =
  POLICY_CANCEL_LOG_DATE_RANGE_PRESETS.reduce((labels, option) => {
    labels[option.value] = option.text;
    return labels;
  }, {});

export const EMPTY_POLICY_CANCEL_LOG_DATE_RANGE_FILTER = {
  preset: null,
  startDate: null,
  endDate: null,
};

function toIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

export function resolvePolicyCancelLogDateRangePreset(preset, referenceDate = new Date()) {
  if (!preset || preset === 'custom') {
    return { startDate: null, endDate: null };
  }

  const today = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate(),
  );

  if (preset === 'today') {
    return {
      startDate: toIsoDate(today),
      endDate: toIsoDate(today),
    };
  }

  if (preset === 'last_7_days') {
    const start = new Date(today);
    start.setDate(start.getDate() - 6);
    return {
      startDate: toIsoDate(start),
      endDate: toIsoDate(today),
    };
  }

  if (preset === 'last_30_days') {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return {
      startDate: toIsoDate(start),
      endDate: toIsoDate(today),
    };
  }

  if (preset === 'this_month') {
    return {
      startDate: toIsoDate(startOfMonth(today)),
      endDate: toIsoDate(endOfMonth(today)),
    };
  }

  if (preset === 'last_month') {
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return {
      startDate: toIsoDate(startOfMonth(lastMonth)),
      endDate: toIsoDate(endOfMonth(lastMonth)),
    };
  }

  return { startDate: null, endDate: null };
}

export function normalizePolicyCancelLogDateRangeFilter(value) {
  if (!value || typeof value !== 'object') {
    return { ...EMPTY_POLICY_CANCEL_LOG_DATE_RANGE_FILTER };
  }

  const preset = value.preset || null;
  const startDate = value.startDate ? formatDateOnlyForPicker(value.startDate) || null : null;
  const endDate = value.endDate ? formatDateOnlyForPicker(value.endDate) || null : null;

  return {
    preset,
    startDate: preset === 'custom' ? startDate : null,
    endDate: preset === 'custom' ? endDate : null,
  };
}

export function arePolicyCancelLogDateRangeFiltersEqual(a, b) {
  const left = normalizePolicyCancelLogDateRangeFilter(a);
  const right = normalizePolicyCancelLogDateRangeFilter(b);
  return (
    left.preset === right.preset &&
    (left.startDate || null) === (right.startDate || null) &&
    (left.endDate || null) === (right.endDate || null)
  );
}

export function resolvePolicyCancelLogDateRangeForFilter(range, referenceDate = new Date()) {
  const normalized = normalizePolicyCancelLogDateRangeFilter(range);
  if (!normalized.preset) {
    return { startDate: null, endDate: null };
  }

  if (normalized.preset !== 'custom') {
    return resolvePolicyCancelLogDateRangePreset(normalized.preset, referenceDate);
  }

  return {
    startDate: normalizeDateOnly(normalized.startDate),
    endDate: normalizeDateOnly(normalized.endDate),
  };
}

export function hasActivePolicyCancelLogDateRangeFilter(range) {
  return Boolean(normalizePolicyCancelLogDateRangeFilter(range).preset);
}

export function getPolicyCancelLogDateRangeFilterLabel(range) {
  const normalized = normalizePolicyCancelLogDateRangeFilter(range);
  if (!normalized.preset) return '';

  if (normalized.preset !== 'custom') {
    return POLICY_CANCEL_LOG_DATE_RANGE_PRESET_LABELS[normalized.preset] || normalized.preset;
  }

  const start = normalized.startDate;
  const end = normalized.endDate;
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Through ${end}`;
  return POLICY_CANCEL_LOG_DATE_RANGE_PRESET_LABELS.custom;
}
